import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, Loader2, MessageSquare, Tag, Undo2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { MethodBadge } from "@/components/Badges";
import { OrderItemRow } from "@/components/OrderItems";
import { LabelDialog } from "@/components/LabelDialog";
import { MessageDialog } from "@/components/MessageDialog";
import { api, errMsg, money, fmtDateTime } from "@/lib/api";

const STATUSES = ["pending", "confirmed", "ready", "completed", "cancelled"];
const EMAIL_NAME = { confirmed: "order-confirmed", ready: "ready-for-pickup" };

const toLocalInput = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
};

const readyLabel = (local) =>
  new Date(local).toLocaleString("en-US", { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });

function EmailNote({ o, form, kind, enabled }) {
  const at = o[`${kind}_notified_at`];
  const failed = String(o[`${kind}_email`] || "").startsWith("failed");
  const tid = `order-${kind}-email`;
  if (at && form.status === o.status) {
    if (failed) return <p className="text-xs text-red-400" data-testid={`${tid}-status`}>{EMAIL_NAME[kind]} email failed. Change the status away and back to {kind} to resend.</p>;
    const sent = o[`${kind}_email`] === "sent";
    return (
      <p className={`text-xs ${sent ? "text-emerald-400" : "text-sky-400"}`} data-testid={`${tid}-status`}>
        {sent ? `${kind === "ready" ? "Ready" : "Confirmation"} email sent to ${o[`${kind}_email_to`] || o.client_email}` : `${kind === "ready" ? "Ready" : "Confirmation"} email sending…`} · {fmtDateTime(at)}
      </p>
    );
  }
  if (form.status === kind && o.status !== kind && (!at || failed)) {
    return (
      <p className="text-xs text-sky-400" data-testid={`${tid}-hint`}>
        {enabled ? `Saving sends ${o.client_email} ${kind === "ready" ? "a" : "an"} ${EMAIL_NAME[kind]} email.` : `Client ${kind} emails are off in Settings.`}
      </p>
    );
  }
  return null;
}

export function OrderDetailDialog({ orderId, onClose, onChanged }) {
  const [o, setO] = useState(null);
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  const [labelTarget, setLabelTarget] = useState(null);
  const [notify, setNotify] = useState({ confirmed: true, ready: true });
  const [msgMode, setMsgMode] = useState(null);

  useEffect(() => {
    api.get("/admin/settings").then((r) => {
      setNotify({ confirmed: r.data.notify_client_confirmed, ready: r.data.notify_client_ready });
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!orderId) {
      setO(null);
      return;
    }
    api.get(`/admin/orders/${orderId}`).then((r) => {
      setO(r.data);
      setForm({ status: r.data.status, payment_status: r.data.payment_status, admin_notes: r.data.admin_notes || "", expected_ready: toLocalInput(r.data.expected_ready_at) });
    }).catch((e) => toast.error(errMsg(e)));
  }, [orderId]);

  const onSaveClick = () => {
    if (form.status !== o.status && ["ready", "completed"].includes(form.status)) setMsgMode("status");
    else save();
  };

  const sendStandalone = async (text) => {
    try {
      const { data } = await api.post(`/admin/orders/${o.id}/message`, { text });
      setO(data);
      toast.success(`Message going to ${o.client_email}`);
      setMsgMode(null);
      onChanged?.();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const resend = async (mid) => {
    try {
      const { data } = await api.post(`/admin/orders/${o.id}/messages/${mid}/resend`);
      setO(data);
      toast.success("Resending message");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const save = async (clientMessage) => {
    setBusy(true);
    try {
      const { expected_ready, ...rest } = form;
      const body = { ...rest };
      if (clientMessage) body.client_message = clientMessage;
      if (expected_ready) {
        body.expected_ready_at = new Date(expected_ready).toISOString();
        body.expected_ready_label = readyLabel(expected_ready);
      } else if (o.expected_ready_at) {
        body.expected_ready_at = "";
      }
      const { data } = await api.patch(`/admin/orders/${o.id}`, body);
      setO(data);
      const kind = form.status !== o.status && ["confirmed", "ready"].includes(form.status) ? form.status : null;
      const emailed = kind && data[`${kind}_notified_at`] && data[`${kind}_notified_at`] !== o[`${kind}_notified_at`];
      toast.success(emailed ? `Order updated · ${kind === "ready" ? "ready" : "confirmation"} email going to ${o.client_email}` : clientMessage ? `Order updated · message going to ${o.client_email}` : "Order updated");
      setMsgMode(null);
      onChanged?.();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const toggleRecon = async (idx, done) => {
    try {
      const { data } = await api.post(`/admin/orders/${o.id}/recon`, { item_index: idx, done });
      setO(data);
      toast.success(done ? "Marked reconstituted · BAC water deducted" : "Reconstitution undone");
      onChanged?.();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const reconAction = (it, idx) => {
    const label = it.category === "peptide" && (
      <Button size="sm" variant="ghost" className="gap-1" onClick={() => setLabelTarget({ orderId: o.id, itemIndex: idx })} data-testid={`order-label-button-${idx}`}><Tag className="h-3.5 w-3.5" />Label</Button>
    );
    if (!it.reconstitute || o.status === "cancelled") return label || null;
    return (
      <>
        {label}
        {it.recon_status === "done" ? (
          <Button size="sm" variant="ghost" onClick={() => toggleRecon(idx, false)} data-testid={`order-recon-undo-${idx}`}><Undo2 className="h-3.5 w-3.5" /></Button>
        ) : (
          <Button size="sm" variant="outline" onClick={() => toggleRecon(idx, true)} data-testid={`order-recon-done-${idx}`} className="gap-1"><Check className="h-3.5 w-3.5" />Done</Button>
        )}
      </>
    );
  };

  return (
    <>
    <LabelDialog target={labelTarget} onClose={() => setLabelTarget(null)} />
    {o && (
      <MessageDialog
        open={!!msgMode}
        title={msgMode === "status" ? `Order ${form.status} — message ${o.client_name}?` : `Message ${o.client_name}`}
        description={msgMode === "status" ? "Add pickup location or how to reach you. It goes out with the status update." : `About order ${o.order_number}`}
        defaultText=""
        skipLabel="Save without message"
        onSkip={msgMode === "status" ? () => save() : null}
        onSend={msgMode === "status" ? (t) => save(t) : sendStandalone}
        onClose={() => setMsgMode(null)}
      />
    )}
    <Dialog open={!!orderId} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto" data-testid="order-detail-dialog">
        {!o ? <Loader2 className="mx-auto h-5 w-5 animate-spin" /> : (
          <>
            <DialogHeader>
              <div className="flex items-start justify-between gap-3 pr-6">
                <DialogTitle className="font-mono-data">{o.order_number}</DialogTitle>
                <Button size="sm" variant="outline" className="gap-1.5" onClick={() => setMsgMode("standalone")} disabled={o.status === "cancelled"} data-testid="order-message-client-button">
                  <MessageSquare className="h-3.5 w-3.5" />Message client
                </Button>
              </div>
              <p className="text-sm text-muted-foreground">{o.client_name} · {o.client_email} · {fmtDateTime(o.created_at)} · {o.source}</p>
            </DialogHeader>
            <div className="rounded-md border border-border px-4">
              {o.items.map((it, idx) => <OrderItemRow key={idx} it={it} action={reconAction(it, idx)} />)}
            </div>
            <div className="flex flex-wrap items-center justify-between gap-2 font-mono-data text-sm">
              <MethodBadge method={o.payment_method} />
              <span>Products {money(o.subtotal)}{o.discount > 0 && <span className="text-emerald-400" data-testid="order-discount"> · {o.promo_code || o.discount_label} −{money(o.discount)}</span>} · Recon {money(o.recon_fees)} · <span className="text-emerald-400">Total {money(o.total)}</span></span>
            </div>
            {o.notes && <p className="rounded-md bg-secondary p-3 text-sm"><span className="text-muted-foreground">Client note: </span>{o.notes}</p>}
            {(o.messages || []).length > 0 && (
              <div className="space-y-2" data-testid="order-messages-list">
                <Label>Messages sent to client</Label>
                {o.messages.map((m) => (
                  <div key={m.id} className="rounded-md border border-border p-3 text-sm">
                    <p className="whitespace-pre-wrap">{m.text}</p>
                    <p className={`mt-1 font-mono-data text-xs ${m.email === "sent" ? "text-emerald-400" : m.email === "sending" ? "text-sky-400" : "text-red-400"}`}>
                      {fmtDateTime(m.created_at)} · email {m.email === "sent" || m.email === "sending" ? m.email : "failed"}
                      {m.email !== "sent" && m.email !== "sending" && (
                        <button type="button" onClick={() => resend(m.id)} className="ml-2 underline" data-testid={`order-message-resend-${m.id}`}>Resend</button>
                      )}
                    </p>
                  </div>
                ))}
              </div>
            )}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Order status</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })} disabled={o.status === "cancelled"}>
                  <SelectTrigger data-testid="order-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s} data-testid={`order-status-option-${s}`}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Payment</Label>
                <Select value={form.payment_status} onValueChange={(v) => setForm({ ...form, payment_status: v })}>
                  <SelectTrigger data-testid="order-payment-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="unpaid" data-testid="order-payment-option-unpaid">unpaid</SelectItem>
                    <SelectItem value="paid" data-testid="order-payment-option-paid">paid</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            {["pending", "confirmed"].includes(form.status) && (
              <div className="space-y-2">
                <Label>Expected ready (shown to client{form.status === "confirmed" ? " and in the confirmation email" : ""})</Label>
                <Input type="datetime-local" value={form.expected_ready} onChange={(e) => setForm({ ...form, expected_ready: e.target.value })} className="font-mono-data" data-testid="order-expected-ready-input" />
              </div>
            )}
            <div className="space-y-2">
              <Label>Private notes (only you see these)</Label>
              <Textarea value={form.admin_notes} onChange={(e) => setForm({ ...form, admin_notes: e.target.value })} data-testid="order-admin-notes-input" />
            </div>
            <EmailNote o={o} form={form} kind="confirmed" enabled={notify.confirmed} />
            <EmailNote o={o} form={form} kind="ready" enabled={notify.ready} />
            {form.status === "cancelled" && o.status !== "cancelled" && (
              <p className="text-xs text-amber-400">Cancelling returns the vials to stock and can't be undone.</p>
            )}
            {["ready", "completed"].includes(form.status) && form.status !== o.status && (
              <p className="text-xs text-muted-foreground">You'll be asked for a pickup / contact message when you save.</p>
            )}
            <Button onClick={onSaveClick} disabled={busy} data-testid="order-save-button">{busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Save changes</Button>
          </>
        )}
      </DialogContent>
    </Dialog>
    </>
  );
}
